import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import { getCommunications, getCommunicationStats, getCommunicationFollowUps, createCommunication, updateCommunication, deleteCommunication, getCases } from '../utils/api';
import { formatDate, formatDateTime, timeAgo } from '../utils/helpers';
import {
  Phone, Mail, FileText, Send, Search, Plus, Trash2, Pencil,
  ChevronLeft, ChevronRight, Clock, AlertCircle, Filter, X,
  MessageSquare, Users, Calendar, ChevronDown, CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

const COMM_TYPES = [
  { value: 'phone',     label: 'Phone Call',   icon: Phone,         color: '#3b82f6' },
  { value: 'email',     label: 'Email',        icon: Mail,          color: '#8b5cf6' },
  { value: 'letter',    label: 'Letter/Mail',  icon: FileText,      color: '#f59e0b' },
  { value: 'fax',       label: 'Fax',          icon: Send,          color: '#64748b' },
  { value: 'text',      label: 'Text Message',  icon: MessageSquare, color: '#22c55e' },
  { value: 'in_person', label: 'In Person',    icon: Users,         color: '#06b6d4' },
  { value: 'portal',    label: 'Client Portal', icon: FileText,     color: '#a78bfa' },
  { value: 'other',     label: 'Other',        icon: FileText,      color: '#94a3b8' },
];

const getTypeConfig = (type) => COMM_TYPES.find(t => t.value === type) || COMM_TYPES[COMM_TYPES.length - 1];

const EMPTY_FORM = {
  case_id: '', direction: 'outbound', comm_type: 'phone', contact_name: '', contact_info: '',
  subject: '', summary: '', duration_minutes: '', outcome: '',
  follow_up_needed: false, follow_up_date: '', follow_up_note: '', comm_date: '',
};

export default function CommunicationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comms, setComms] = useState([]);
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const sentinelRef = useRef(null);
  const PER_PAGE = 40;

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [dirFilter, setDirFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [showFollowUps, setShowFollowUps] = useState(false);

  // Modal
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [cases, setCases] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editCaseInfo, setEditCaseInfo] = useState(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseDropOpen, setCaseDropOpen] = useState(false);
  const [casesLoading, setCasesLoading] = useState(false);
  const caseDropRef = useRef(null);
  const caseSearchTimer = useRef(null);

  // Close case dropdown on outside click
  useEffect(() => {
    if (!caseDropOpen) return;
    const handler = (e) => { if (caseDropRef.current && !caseDropRef.current.contains(e.target)) setCaseDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [caseDropOpen]);

  // Search cases from server with debounce
  const searchCases = useCallback(async (query) => {
    setCasesLoading(true);
    try {
      const params = { per_page: 20 };
      if (query.trim()) params.search = query.trim();
      const res = await getCases(params);
      setCases(Array.isArray(res) ? res : (res?.cases || []));
    } catch { setCases([]); }
    finally { setCasesLoading(false); }
  }, []);

  const loadComms = useCallback(async (pg = 1, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = { page: pg, limit: PER_PAGE };
      if (typeFilter) params.comm_type = typeFilter;
      if (dirFilter) params.direction = dirFilter;
      if (searchQ.trim()) params.search = searchQ.trim();
      if (showFollowUps) params.follow_up = 'true';
      const res = await getCommunications(params);
      if (append) {
        setComms(prev => [...prev, ...(res.data || [])]);
      } else {
        setComms(res.data || []);
      }
      setTotal(res.total || 0);
      setTotalPages(Math.ceil((res.total || 0) / PER_PAGE));
      setPage(pg);
    } catch { if (!append) toast.error('Failed to load communications'); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [typeFilter, dirFilter, searchQ, showFollowUps]);

  const loadStats = useCallback(async () => {
    try {
      const s = await getCommunicationStats();
      setStats(s);
    } catch {}
  }, []);

  const loadFollowUps = useCallback(async () => {
    try {
      const data = await getCommunicationFollowUps();
      setFollowUps(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => { setComms([]); setPage(1); loadComms(1); }, [loadComms]);
  useEffect(() => { loadStats(); loadFollowUps(); }, [loadStats, loadFollowUps]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !loadingMore && page < totalPages) {
        loadComms(page + 1, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page, totalPages, loading, loadingMore, loadComms]);

  const openNew = async () => {
    setEditId(null);
    setEditCaseInfo(null);
    setForm({ ...EMPTY_FORM, comm_date: new Date().toISOString().slice(0, 16) });
    setCaseSearch('');
    setCaseDropOpen(false);
    await searchCases('');
    setShowForm(true);
  };

  const openEdit = (comm) => {
    setEditId(comm._id || comm.id);
    setEditCaseInfo(comm.case ? { client_name: comm.case.client_name, case_number: comm.case.case_number } : null);
    setForm({
      case_id: comm.case_id || '',
      direction: comm.direction || 'outbound',
      comm_type: comm.comm_type || 'phone',
      contact_name: comm.contact_name || '',
      contact_info: comm.contact_info || '',
      subject: comm.subject || '',
      summary: comm.summary || '',
      duration_minutes: comm.duration_minutes ?? '',
      outcome: comm.outcome || '',
      follow_up_needed: comm.follow_up_needed || false,
      follow_up_date: comm.follow_up_date || '',
      follow_up_note: comm.follow_up_note || '',
      comm_date: comm.comm_date ? comm.comm_date.slice(0, 16) : '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.case_id) return toast.error('Please select a case');
    if (!form.comm_type) return toast.error('Communication type is required');
    setSaving(true);
    try {
      if (editId) {
        await updateCommunication(editId, form);
        toast.success('Communication updated');
      } else {
        await createCommunication(form);
        toast.success('Communication logged');
      }
      setShowForm(false);
      setComms([]); setPage(1); loadComms(1);
      loadStats();
      loadFollowUps();
    } catch (err) { toast.error(err?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCommunication(id);
      toast.success('Communication deleted');
      setComms([]); setPage(1); loadComms(1); loadStats(); loadFollowUps();
    } catch { toast.error('Failed to delete'); }
    setConfirmDelete(null);
  };

  const pendingFollowUps = followUps.filter(f => {
    if (!f.follow_up_date) return true;
    return new Date(f.follow_up_date) <= new Date();
  });

  return (
    <>
      <Header title="Communications">
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          <Plus size={14} /> Log Communication
        </button>
      </Header>

      <div className="app-content">
        {/* Stats */}
        {stats && (
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
            <div className="stat-card">
              <span className="stat-label">Total Logged</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-card" style={{ borderLeft: '3px solid #3b82f6' }}>
              <span className="stat-label">Last 30 Days</span>
              <span className="stat-value">{stats.last_30_days}</span>
            </div>
            <div className="stat-card" style={{ borderLeft: '3px solid #f97316' }}>
              <span className="stat-label">Pending Follow-ups</span>
              <span className="stat-value" style={{ color: stats.pending_follow_ups > 0 ? '#f97316' : 'inherit' }}>{stats.pending_follow_ups}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">By Type (30d)</span>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                {Object.entries(stats.by_type || {}).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([type, count]) => {
                  const tc = getTypeConfig(type);
                  return <span key={type} style={{ fontSize: '0.68rem', fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: `${tc.color}15`, color: tc.color }}>{count} {tc.label}</span>;
                })}
              </div>
            </div>
          </div>
        )}

        {/* Follow-ups banner */}
        {pendingFollowUps.length > 0 && (
          <div className="card" style={{ marginBottom: '16px', borderLeft: '3px solid #f97316', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} style={{ color: '#f97316' }} />
              <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                {pendingFollowUps.length} follow-up{pendingFollowUps.length !== 1 ? 's' : ''} due
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowFollowUps(!showFollowUps)} style={{ marginLeft: 'auto', fontSize: '0.78rem' }}>
                {showFollowUps ? 'Show All' : 'Show Follow-ups Only'}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)' }}>
              <Search size={14} style={{ color: 'var(--text-muted)' }} />
              <input style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.85rem', flex: 1, fontFamily: 'inherit' }}
                placeholder="Search contacts, subjects..."
                value={searchQ} onChange={(e) => { setSearchQ(e.target.value); setPage(1); }}
              />
            </div>
            <select className="form-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} style={{ width: 'auto', fontSize: '0.82rem' }}>
              <option value="">All Types</option>
              {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="form-select" value={dirFilter} onChange={(e) => { setDirFilter(e.target.value); setPage(1); }} style={{ width: 'auto', fontSize: '0.82rem' }}>
              <option value="">All Directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
            {(typeFilter || dirFilter || searchQ || showFollowUps) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setTypeFilter(''); setDirFilter(''); setSearchQ(''); setShowFollowUps(false); setPage(1); }}>
                <X size={13} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Communications list */}
        <div className="card">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" /></div>
          ) : comms.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Phone size={36} style={{ marginBottom: '10px', opacity: 0.3 }} />
              <p style={{ fontSize: '0.92rem', marginBottom: '12px' }}>No communications logged yet</p>
              <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={14} /> Log First Communication</button>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '32px' }}></th>
                      <th>Date</th>
                      <th>Claimant</th>
                      <th>Contact</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Logged By</th>
                      <th style={{ width: '32px' }}></th>
                      <th style={{ width: '80px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {comms.map(c => {
                      const tc = getTypeConfig(c.comm_type);
                      const Icon = tc.icon;
                      return (
                        <tr key={c._id} onClick={() => openEdit(c)} style={{ cursor: 'pointer' }}>
                          <td>
                            {c.direction === 'inbound'
                              ? <ChevronLeft size={14} style={{ color: '#22c55e' }} title="Inbound" />
                              : <ChevronRight size={14} style={{ color: '#3b82f6' }} title="Outbound" />
                            }
                          </td>
                          <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {c.comm_date ? formatDateTime(c.comm_date) : '--'}
                          </td>
                          <td>
                            {c.case ? (
                              <Link to={`/cases/${c.case.id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--zeus-gold)', textDecoration: 'none', fontWeight: 500, fontSize: '0.82rem' }}>
                                {c.case.client_name || c.case.case_number}
                              </Link>
                            ) : '--'}
                          </td>
                          <td>
                            <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{c.contact_name || '--'}</div>
                            {c.contact_info && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{c.contact_info}</div>}
                          </td>
                          <td>
                            <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>{c.subject || '--'}</div>
                            {c.summary && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.summary}</div>}
                          </td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: '8px', background: `${tc.color}15`, color: tc.color }}>
                              <Icon size={11} /> {tc.label}
                            </span>
                            {c.follow_up_needed && (
                              <span style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: '#f97316', marginTop: '2px' }}>Follow-up needed</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{c.logged_by_name || '--'}</td>
                          <td>
                            {c.duration_minutes && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{c.duration_minutes}m</span>}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} title="Edit">
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => setConfirmDelete(c._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }} title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} style={{ padding: '16px', textAlign: 'center' }}>
                {loadingMore && <div className="spinner" style={{ margin: '0 auto' }} />}
                {!loadingMore && page < totalPages && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Scroll for more...</span>
                )}
                {!loadingMore && page >= totalPages && comms.length > 0 && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Showing all {total.toLocaleString()} communication{total !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Log/Edit Communication Modal ── */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editId ? 'Edit Communication' : 'Log Communication'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Show claimant info when editing */}
          {editId && editCaseInfo && (
            <div>
              <label className="form-label">Linked Case</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', background: 'var(--zeus-gold-dim)', border: '1px solid rgba(212,168,83,0.2)' }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--zeus-gold)' }}>
                  {editCaseInfo.case_number}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {editCaseInfo.client_name}
                </span>
              </div>
            </div>
          )}

          {/* Case selector — searchable */}
          {!editId && (
            <div ref={caseDropRef} style={{ position: 'relative' }}>
              <label className="form-label">Case <span style={{ color: 'var(--status-red)' }}>*</span></label>
              <input
                className="form-input"
                placeholder="Search by case # or client name..."
                value={caseSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setCaseSearch(val);
                  setCaseDropOpen(true);
                  setForm(p => ({ ...p, case_id: '' }));
                  clearTimeout(caseSearchTimer.current);
                  caseSearchTimer.current = setTimeout(() => searchCases(val), 300);
                }}
                onFocus={() => setCaseDropOpen(true)}
                autoComplete="off"
              />
              {form.case_id && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', padding: '6px 10px', borderRadius: '8px', background: 'var(--zeus-gold-dim)', border: '1px solid rgba(212,168,83,0.2)' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--zeus-gold)' }}>
                    {cases.find(c => (c.id || c._id) === form.case_id)?.case_number || ''}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {cases.find(c => (c.id || c._id) === form.case_id)?.client_name || ''}
                  </span>
                  <button onClick={() => { setForm(p => ({ ...p, case_id: '' })); setCaseSearch(''); }}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}>
                    <X size={14} />
                  </button>
                </div>
              )}
              {caseDropOpen && !form.case_id && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: 'var(--zeus-navy, #0f1a2e)', border: '1px solid var(--zeus-border)',
                  borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  maxHeight: '200px', overflowY: 'auto', marginTop: '4px',
                }}>
                  {casesLoading && (
                    <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Searching...</div>
                  )}
                  {!casesLoading && cases
                    .slice(0, 20)
                    .map(c => (
                      <div key={c.id || c._id}
                        onClick={() => {
                          setForm(p => ({ ...p, case_id: c.id || c._id }));
                          setCaseSearch(`${c.case_number} - ${c.client_name}`);
                          setCaseDropOpen(false);
                        }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                          borderBottom: '1px solid rgba(255,255,255,0.03)',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--zeus-gold)' }}>{c.case_number}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{c.client_name}</span>
                      </div>
                    ))
                  }
                  {!casesLoading && cases.length === 0 && (
                    <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No cases found</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Direction + Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label className="form-label">Direction <span style={{ color: 'var(--status-red)' }}>*</span></label>
              <select className="form-select" value={form.direction} onChange={(e) => setForm(p => ({ ...p, direction: e.target.value }))}>
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
              </select>
            </div>
            <div>
              <label className="form-label">Type <span style={{ color: 'var(--status-red)' }}>*</span></label>
              <select className="form-select" value={form.comm_type} onChange={(e) => setForm(p => ({ ...p, comm_type: e.target.value }))}>
                {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Contact + Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label className="form-label">Contact Name</label>
              <input className="form-input" value={form.contact_name} onChange={(e) => setForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Who was contacted..." />
            </div>
            <div>
              <label className="form-label">Contact Info</label>
              <input className="form-input" value={form.contact_info} onChange={(e) => setForm(p => ({ ...p, contact_info: e.target.value }))} placeholder="Phone, email..." />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label className="form-label">Date/Time</label>
              <input className="form-input" type="datetime-local" value={form.comm_date} onChange={(e) => setForm(p => ({ ...p, comm_date: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Duration (minutes)</label>
              <input className="form-input" type="number" value={form.duration_minutes} onChange={(e) => setForm(p => ({ ...p, duration_minutes: e.target.value }))} placeholder="0" />
            </div>
          </div>

          {/* Subject + Summary */}
          <div>
            <label className="form-label">Subject</label>
            <input className="form-input" value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Brief subject line..." />
          </div>
          <div>
            <label className="form-label">Summary</label>
            <textarea className="form-input" rows={3} value={form.summary} onChange={(e) => setForm(p => ({ ...p, summary: e.target.value }))} placeholder="What was discussed..." style={{ resize: 'vertical' }} />
          </div>
          <div>
            <label className="form-label">Outcome</label>
            <input className="form-input" value={form.outcome} onChange={(e) => setForm(p => ({ ...p, outcome: e.target.value }))} placeholder="Result or next step..." />
          </div>

          {/* Follow-up */}
          <div style={{ padding: '12px', background: 'rgba(0,0,0,0.1)', borderRadius: 'var(--radius-sm)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              <input type="checkbox" checked={form.follow_up_needed} onChange={(e) => setForm(p => ({ ...p, follow_up_needed: e.target.checked }))} />
              Follow-up needed
            </label>
            {form.follow_up_needed && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginTop: '10px' }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>Follow-up Date</label>
                  <input className="form-input" type="date" value={form.follow_up_date} onChange={(e) => setForm(p => ({ ...p, follow_up_date: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>Follow-up Note</label>
                  <input className="form-input" value={form.follow_up_note} onChange={(e) => setForm(p => ({ ...p, follow_up_note: e.target.value }))} placeholder="What to follow up on..." />
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update' : 'Log Communication'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDelete(confirmDelete)}
        title="Delete Communication"
        message="Are you sure you want to delete this communication log? This cannot be undone."
        confirmLabel="Delete"
        danger
      />
    </>
  );
}
